import { Link } from 'react-router';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';

type Featured = {
  slug: string;
  title: string;
  description: string;
  date: string;
  tags?: string[];
  cover?: string;
} | null;

type Props = {
  featured: Featured;
  totalArticles: number;
  issueNumber?: number;
};

function formatDate(d: string) {
  try {
    return new Date(d)
      .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      .toUpperCase();
  } catch {
    return d;
  }
}

function FeaturedGradient({ label, cover }: { label: string; cover?: string }) {
  return (
    <div className="relative flex h-48 items-center justify-center overflow-hidden bg-[conic-gradient(from_135deg,#84CC16_0deg,#1A1A2E_90deg,#EA580C_180deg,#84CC16_270deg,#1A1A2E_360deg)] md:h-60">
      {cover && (
        <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
      <div className="absolute left-5 top-5 inline-flex items-center gap-1.5 border-2 border-charcoal-800 bg-lime-500 px-3 py-1 font-marketing-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-charcoal-800 shadow-neobrutal-2">
        À la une
      </div>
      <div className="absolute bottom-5 right-5 border-2 border-charcoal-800 bg-white px-3 py-1 font-marketing-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-charcoal-800 shadow-neobrutal-2">
        {label}
      </div>
      {!cover && (
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(245,243,239,0.35),transparent_70%)]" />
      )}
    </div>
  );
}

export default function BlogHeroMagazineV2({ featured, totalArticles, issueNumber = 42 }: Props) {
  const month = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const monthCap = month.charAt(0).toUpperCase() + month.slice(1);

  return (
    <section className="bg-paper pb-12 pt-16 md:pb-16 md:pt-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-end gap-12 md:grid-cols-[1fr_1fr] md:gap-16">
          <div>
            <MarketingHeroPill>
              Le Journal Velko · N° {issueNumber.toString().padStart(3, '0')}
            </MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(48px,6.8vw,100px)] font-bold leading-[0.92] tracking-[-0.045em] text-charcoal-800">
              Conseils.
              <br />
              <span className="underline-lime">Pas d'angle mort.</span>
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
              Guides pratiques, retours de commerçants, actus produit. Écrit par l'équipe Velko et
              des commerçants qui vivent le terrain africain.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-5 border-t-2 border-dashed border-charcoal-200 pt-5 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
              <span>{monthCap}</span>
              <span className="h-3 w-px bg-charcoal-300" />
              <span>
                {totalArticles} article{totalArticles > 1 ? 's' : ''} · ~10 min de lecture
              </span>
            </div>
          </div>

          {featured ? (
            <Link
              to={`/blog/${featured.slug}`}
              className="group flex flex-col border-2 border-charcoal-800 bg-white shadow-neobrutal-10 transition-transform hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-neobrutal-14"
            >
              <FeaturedGradient label={featured.tags?.[0] ?? 'Guide'} cover={featured.cover} />
              <div className="border-t-2 border-charcoal-800 p-6 md:p-7">
                <h2 className="font-marketing-display text-[clamp(22px,2.4vw,32px)] font-bold leading-[1.1] tracking-[-0.025em] text-charcoal-800">
                  {featured.title}
                </h2>
                <p className="mt-4 font-marketing-body text-[15px] leading-relaxed text-charcoal-600">
                  {featured.description}
                </p>
                <div className="mt-5 flex items-center justify-between border-t-2 border-dashed border-charcoal-200 pt-4 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                  <span>{formatDate(featured.date)} · Guide</span>
                  <span className="flex h-8 w-8 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800 transition-transform group-hover:-translate-x-0.5 group-hover:-translate-y-0.5">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          ) : (
            <div className="border-2 border-charcoal-800 bg-white p-8 text-center font-marketing-body text-charcoal-500 shadow-neobrutal-6">
              Aucun article pour l'instant — reviens bientôt.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
